<template>
    <div class="connection-detail">
        <o-bread-crumb>
            <template v-slot:pageName>
                Connection Apps
            </template>
            <template v-slot:pageDetail>
                Connection Detail
            </template>
            <template v-slot:pageDescription>
                <button class="btn-back" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Back to List
                </button>
            </template>
        </o-bread-crumb>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="loading-text">Loading connection details...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="loadError" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ loadError }}
        </div>

        <!-- Content -->
        <div v-else-if="connection" class="detail-content">
            <div class="cards-container">
                <!-- Connection Info Card -->
                <div class="info-card">
                    <div class="card-header">
                        <div class="card-header-left">
                            <i class="fas fa-plug"></i>
                            <h4>Connection Information</h4>
                        </div>
                        <span :class="['status-badge', statusClass]">
                            {{ connection.status }}
                        </span>
                    </div>

                    <!-- Divider -->
                    <div class="card-divider"></div>

                    <div class="card-body">
                        <!-- Read-only Information -->
                        <div class="info-section">
                            <h5>Branch & Merchant Details</h5>
                            <div class="info-grid">
                                <div class="info-item">
                                    <label>Branch Name</label>
                                    <div class="value">{{ branchName }}</div>
                                </div>
                                <div class="info-item">
                                    <label>Product ID</label>
                                    <div class="value-with-copy">
                                        <div class="value">{{ productID }}</div>
                                        <button @click="copyProductId" class="btn-copy-small" title="Copy Product ID">
                                            <i class="fas fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="info-item">
                                    <label>Merchant Name</label>
                                    <div class="value">{{ merchantName }}</div>
                                </div>
                                <div class="info-item">
                                    <label>X-Tenant-Domain</label>
                                    <div class="value-with-copy">
                                        <div class="value">{{ tenantDomain }}</div>
                                        <button @click="copyTenantDomain" class="btn-copy-small"
                                            title="Copy X-Tenant-Domain">
                                            <i class="fas fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Editable Fields -->
                        <div class="info-section">
                            <h5>Connection Configuration</h5>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="property-id">Property ID</label>
                                    <input id="property-id" type="text" v-model="editData.property_id"
                                        class="form-control" :disabled="isSaving" @keypress="onlyNumbers"
                                        inputmode="numeric" />
                                </div>

                                <div class="form-group">
                                    <label for="status">Status</label>
                                    <select id="status" v-model="editData.status" class="form-control"
                                        :disabled="isSaving">
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group">
                                <label>Authentication URL (Auto-generated)</label>
                                <div class="auth-url-display">
                                    <input type="text" :value="authUrl" class="form-control" readonly />
                                    <button @click="copyAuthUrl" class="btn-copy" title="Copy URL">
                                        <i class="fas fa-copy"></i>
                                    </button>
                                </div>
                            </div>

                            <div class="form-actions">
                                <button @click="saveChanges" class="btn-primary" :disabled="!canSave">
                                    <span v-if="isSaving">
                                        <i class="fas fa-spinner fa-spin me-1"></i>
                                        Saving...
                                    </span>
                                    <span v-else>
                                        <i class="fas fa-save me-1"></i>
                                        Save Changes
                                    </span>
                                </button>
                                <button v-if="hasChanges" @click="cancelEdit" class="btn-secondary"
                                    :disabled="isSaving">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions Card -->
                <div class="actions-card">
                    <div class="card-header">
                        <div class="card-header-left">
                            <i class="fas fa-cog"></i>
                            <h4>Advanced Actions</h4>
                        </div>
                    </div>

                    <!-- Divider -->
                    <div class="card-divider"></div>

                    <div class="card-body">
                        <div class="action-item">
                            <div class="action-info">
                                <h5>Regenerate Personal Access Token</h5>
                                <p>Generate a new personal access token. The old token will be immediately revoked. You can <a href="#" @click.prevent="showTokenDetailModal = true" class="link-primary">click here</a> to see details of the existing token first.</p>
                            </div>
                            <button @click="showRegenerateModal = true" class="btn-warning">
                                <i class="fas fa-sync-alt me-1"></i>
                                Regenerate Token
                            </button>
                        </div>

                        <div class="action-item danger">
                            <div class="action-info">
                                <h5>Delete Connection</h5>
                                <p>Permanently delete this connection. This action cannot be undone.</p>
                            </div>
                            <button @click="showDeleteModal = true" class="btn-danger">
                                <i class="fas fa-trash-alt me-1"></i>
                                Delete Connection
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Regenerate Token Modal -->
        <div v-if="showRegenerateModal" class="modal-overlay" @click.self="showRegenerateModal = false">
            <div class="modal-content">
                <h4>Regenerate Access Token</h4>
                <p>Enter a name for the new token:</p>
                <input v-model="newTokenName" type="text" class="form-control" placeholder="Enter token name" />
                <div class="modal-actions">
                    <button @click="regenerateToken" class="btn-primary" :disabled="!newTokenName || isRegenerating">
                        <span v-if="isRegenerating">
                            <i class="fas fa-spinner fa-spin me-1"></i>
                            Regenerating...
                        </span>
                        <span v-else>Regenerate</span>
                    </button>
                    <button @click="showRegenerateModal = false" class="btn-secondary">Cancel</button>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
            <div class="modal-content">
                <h4>Delete Connection</h4>
                <div class="alert alert-danger" style="margin-bottom: 1rem; display: flex; gap: 0.5rem; align-items: flex-start;">
                    <i class="fas fa-exclamation-triangle" style="margin-top: 3px;"></i>
                    <div>
                        <strong>Warning: This action cannot be undone.</strong>
                    </div>
                </div>
                <p>Deleting this connection will permanently remove/revoke the following resources:</p>
                <ul class="delete-list">
                    <li>
                        <i class="fas fa-user-slash"></i> 
                        <div><strong>Token Authority:</strong> The dedicated user account created for this connection.</div>
                    </li>
                    <li>
                        <i class="fas fa-key"></i> 
                        <div><strong>Access Token:</strong> The active API token will be revoked immediately.</div>
                    </li>
                    <li>
                        <i class="fas fa-box"></i> 
                        <div><strong>Product:</strong> The product associated with this connection.</div>
                    </li>
                    <li>
                        <i class="fas fa-link"></i> 
                        <div><strong>Connection Config:</strong> All settings and property ID mappings.</div>
                    </li>
                    <li>
                        <i class="fas fa-globe"></i> 
                        <div><strong>Auth URL:</strong> The Auth URL in business profile will be cleared.</div>
                    </li>
                </ul>
                <div class="modal-actions">
                    <button @click="deleteConnection" class="btn-danger" :disabled="isDeleting">
                        <span v-if="isDeleting">
                            <i class="fas fa-spinner fa-spin me-1"></i>
                            Deleting...
                        </span>
                        <span v-else>Yes, Delete this connection</span>
                    </button>
                    <button @click="showDeleteModal = false" class="btn-secondary">Cancel</button>
                </div>
            </div>
        </div>

        <!-- New Token Display Modal -->
        <div v-if="showNewTokenModal" class="modal-overlay" @click.self="showNewTokenModal = false">
            <div class="modal-content">
                <h4>New Access Token Generated</h4>
                <p class="text-warning">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    Save this token now - it won't be shown again!
                </p>
                <div class="token-display">
                    <input type="text" :value="newToken" class="form-control" readonly />
                    <button @click="copyNewToken" class="btn-copy">
                        <i class="fas fa-copy"></i>
                    </button>
                </div>
                <div class="modal-actions">
                    <button @click="showNewTokenModal = false" class="btn-primary">Done</button>
                </div>
            </div>
        </div>

        <!-- Token Detail Modal -->
        <div v-if="showTokenDetailModal" class="modal-overlay" @click.self="showTokenDetailModal = false">
            <div class="modal-content modal-large">
                <!-- Personal Access Token Details -->
                <h4>Personal Access Token Details</h4>
                <div v-if="connection && connection.access_token" class="token-table-container">
                    <table class="token-detail-table">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="field-name">Token Name</td>
                                <td class="field-value">{{ connection.access_token.name }}</td>
                            </tr>
                            <tr>
                                <td class="field-name">Abilities</td>
                                <td class="field-value">
                                    <span v-if="connection.access_token.abilities && connection.access_token.abilities.length > 0">
                                        {{ connection.access_token.abilities.join(', ') }}
                                    </span>
                                    <span v-else class="text-muted">No specific abilities</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="field-name">Created At</td>
                                <td class="field-value">{{ formatDate(connection.access_token.created_at) }}</td>
                            </tr>
                            <tr>
                                <td class="field-name">Expires At</td>
                                <td class="field-value">
                                    <span v-if="connection.access_token.expires_at">
                                        {{ formatDate(connection.access_token.expires_at) }}
                                    </span>
                                    <span v-else class="text-muted">No expiration</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="field-name">Created By</td>
                                <td class="field-value">
                                    <span v-if="connection.access_token.created_by">
                                        {{ connection.access_token.created_by }}
                                    </span>
                                    <span v-else class="text-muted">No created by</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <!-- Token Authority Details -->
                <div v-if="connection && connection.service_account">
                    <hr class="section-divider">
                    <h4>Token Authority Details</h4>
                    <div class="token-table-container">
                        <table class="token-detail-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="field-name">Account Name</td>
                                    <td class="field-value">{{ connection.service_account.name }}</td>
                                </tr>
                                <tr>
                                    <td class="field-name">Email</td>
                                    <td class="field-value">{{ connection.service_account.email }}</td>
                                </tr>
                                <tr>
                                    <td class="field-name">Role</td>
                                    <td class="field-value">
                                        <div v-if="!isEditingRole" class="role-display">
                                            <span class="role-badge">{{ connection.service_account.role }}</span>
                                            <button @click="enableEditRole" class="btn-icon-small" title="Edit Role">
                                                <i class="fas fa-pencil-alt"></i>
                                            </button>
                                        </div>
                                        <div v-else class="role-edit">
                                            <select v-model="selectedRole" class="role-select">
                                                <option v-for="role in availableRoles" :key="role.value" :value="role.text">
                                                    {{ role.text }}
                                                </option>
                                            </select>
                                            <div class="role-actions-row">
                                                <button @click="updateRole" :disabled="isUpdatingRole" class="btn-xs btn-primary">
                                                    <i v-if="isUpdatingRole" class="fas fa-spinner fa-spin"></i>
                                                    <span v-else>Save</span>
                                                </button>
                                                <button @click="cancelEditRole" :disabled="isUpdatingRole" class="btn-xs btn-secondary">
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                        <!-- Warning for high privilege role -->
                                        <div v-if="isEditingRole && isHighPrivilegeRole" class="role-warning-small">
                                            <i class="fas fa-exclamation-triangle"></i>
                                            High privilege role
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="field-name">Status</td>
                                    <td class="field-value">
                                        <span :class="['status-badge-small', connection.service_account.status === 'Active' ? 'status-active' : 'status-inactive']">
                                            {{ connection.service_account.status }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="!connection?.access_token && !connection?.service_account" class="no-token-info">
                    <i class="fas fa-info-circle"></i>
                    <p>No token information available</p>
                </div>
                <div class="modal-actions">
                    <button @click="showTokenDetailModal = false" class="btn-secondary">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { OBreadCrumb } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'

export default {
    name: 'ConnectionDetail',
    components: {
        OBreadCrumb
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const store = useStore()

        const connection = ref(null)
        const isLoading = ref(true)
        const loadError = ref('')
        const isSaving = ref(false)
        const isRegenerating = ref(false)
        const isDeleting = ref(false)
        const bookingEngineApi = ref('')

        const { showToast } = useToast()

        const editData = ref({
            property_id: '',
            status: 'active'
        })

        const originalData = ref({})

        const showRegenerateModal = ref(false)
        const showDeleteModal = ref(false)
        const showNewTokenModal = ref(false)
        const showTokenDetailModal = ref(false)
        const newTokenName = ref('')
        const newToken = ref('')

        const branchName = computed(() => connection.value?.branch?.name || '-')
        const branchCode = computed(() => connection.value?.branch?.code || '-')
        const merchantName = computed(() => connection.value?.merchant?.name || '-')
        const productID = computed(() => connection.value?.product?.id || '-')
        const tenantDomain = computed(() => {
            return store.state.tenant?.domain || window.location.hostname || '-'
        })

        const authUrl = computed(() => {
            if (!editData.value.property_id) return ''
            if (!bookingEngineApi.value) return 'Loading...'
            return `${bookingEngineApi.value}${editData.value.property_id}/`
        })

        const statusClass = computed(() => {
            return editData.value.status === 'active' ? 'status-active' : 'status-inactive'
        })

        const hasChanges = computed(() => {
            return editData.value.property_id != originalData.value.property_id ||
                editData.value.status !== originalData.value.status
        })

        const canSave = computed(() => {
            return hasChanges.value && !isSaving.value
        })


        async function loadConnection() {
            isLoading.value = true
            loadError.value = ''

            try {
                const response = await store.dispatch('ConnectionApps/read_single', route.params.id)
                connection.value = { ...response }

                editData.value = {
                    property_id: response.property_id,
                    status: response.status
                }

                originalData.value = { ...editData.value }
            } catch (err) {
                console.error('Error loading connection:', err)
                loadError.value = err.response?.data?.message || 'Failed to load connection details'
            } finally {
                isLoading.value = false
            }
        }

        const userInfo = computed(() => store.getters['Auth/user'])

        const isEditingRole = ref(false)
        const selectedRole = ref('')
        const isUpdatingRole = ref(false)

        const availableRoles = ref([])
        
        const isHighPrivilegeRole = computed(() => {
            if (!selectedRole.value) return false
            const role = selectedRole.value.toLowerCase()
            return role.includes('root') || role.includes('corporate') || role.includes('branch') || role.includes('unit')  
        })

        async function enableEditRole() {
            isEditingRole.value = true
            selectedRole.value = connection.value.service_account.role
            
            if (availableRoles.value.length === 0) {
                try {
                    const corporateId = connection.value?.branch?.corporate_id || userInfo.value?.corporate_id
                    
                    if (corporateId) {
                        await store.dispatch('Role/read_all', corporateId)
                        const rolesData = store.state.Role?.roles || []
                        availableRoles.value = rolesData.map(entity => ({ 
                            value: entity.id, 
                            text: entity.name 
                        }))
                    } else {
                         console.warn('Corporate ID not found via connection or user info')
                    }
                } catch (err) {
                    console.error('Failed to load roles:', err)
                    showToast({
                        type: 'error',
                        title: 'Load Failed',
                        message: 'Failed to load available roles'
                    })
                }
            }
        }

        function cancelEditRole() {
            isEditingRole.value = false
            selectedRole.value = ''
        }

        async function updateRole() {
            if (!selectedRole.value) return
            
            isUpdatingRole.value = true
            try {
                await store.dispatch('ConnectionApps/updateServiceAccountRole', {
                    id: route.params.id,
                    roleName: selectedRole.value
                })
                
                showToast({
                    type: 'success',
                    title: 'Role Updated',
                    message: 'Token authority role updated successfully'
                })
                
                // Reload connection to get updated data
                await loadConnection()
                isEditingRole.value = false
            } catch (err) {
                console.error('Error updating role:', err)
                showToast({
                    type: 'error',
                    title: 'Update Failed',
                    message: err.response?.data?.message || 'Failed to update role'
                })
            } finally {
                isUpdatingRole.value = false
            }
        }

        /**
         * Format date to readable format
         * @param {String} dateString - ISO date string
         * @returns {String} Formatted date
         */
        function formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }

        async function saveChanges() {
            isSaving.value = true

            try {
                await store.dispatch('ConnectionApps/update', {
                    id: route.params.id,
                    data: editData.value
                })

                originalData.value = { ...editData.value }

                showToast({
                    type: 'success',
                    title: 'Update Successful',
                    message: 'Connection updated successfully'
                })

                // Reload connection to get updated data
                await loadConnection()
            } catch (err) {
                console.error('Error updating connection:', err)
                showToast({
                    type: 'error',
                    title: 'Update Failed',
                    message: err.response?.data?.message || 'Failed to update connection'
                })
            } finally {
                isSaving.value = false
            }
        }

        function cancelEdit() {
            editData.value = { ...originalData.value }
        }

        /**
         * Handle regenerate token action
         * Shows confirmation dialog before regeneration
         */
        async function regenerateToken() {
            if (!newTokenName.value) return

            isRegenerating.value = true

            try {
                const response = await store.dispatch('ConnectionApps/regenerateToken', {
                    id: route.params.id,
                    tokenName: newTokenName.value
                })

                newToken.value = response.data.data.access_token
                showRegenerateModal.value = false
                showNewTokenModal.value = true
                newTokenName.value = ''

                showToast({
                    type: 'success',
                    title: 'Token Regenerated',
                    message: 'Access token regenerated successfully'
                })

                await new Promise(resolve => setTimeout(resolve, 1000))
                await loadConnection()
            } catch (err) {
                console.error('Error regenerating token:', err)
                showToast({
                    type: 'error',
                    title: 'Regeneration Failed',
                    message: err.response?.data?.message || 'Failed to regenerate token'
                })
            } finally {
                isRegenerating.value = false
            }
        }

        /**
         * Handle delete connection action
         * Shows confirmation dialog before deletion
         */
        async function deleteConnection() {
            isDeleting.value = true

            try {
                await store.dispatch('ConnectionApps/delete', route.params.id)

                showToast({
                    type: 'success',
                    title: 'Deletion Successful',
                    message: 'Connection deleted successfully'
                })

                setTimeout(() => {
                    router.push({ name: 'ConnectionApps' })
                }, 1500)
            } catch (err) {
                console.error('Error deleting connection:', err)
                showToast({
                    type: 'error',
                    title: 'Deletion Failed',
                    message: err.response?.data?.message || 'Failed to delete connection'
                })
                isDeleting.value = false
            }
        }

        async function copyAuthUrl() {
            try {
                await navigator.clipboard.writeText(authUrl.value)
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Authentication URL copied to clipboard'
                })
            } catch (err) {
                showToast({
                    type: 'error',
                    title: 'Copy Failed',
                    message: 'Failed to copy URL'
                })
            }
        }

        /**
         * Copies the newly generated token to the clipboard.
         * @returns {Promise<void>}
         */
        async function copyNewToken() {
            try {
                await navigator.clipboard.writeText(newToken.value)
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Token copied to clipboard'
                })
            } catch (err) {
                showToast({
                    type: 'error',
                    title: 'Copy Failed',
                    message: 'Failed to copy token'
                })
            }
        }

        /**
         * Handle back button click
         * Navigates back to the connection list
         */
        function goBack() {
            router.push({ name: 'ConnectionApps' })
        }


        /**
         * Copies the product ID to the clipboard.
         * @returns {Promise<void>}
         */
        async function copyProductId() {
            try {
                const productId = connection.value?.product_id || ''
                if (!productId) {
                    showToast({
                        type: 'warning',
                        title: 'Not Available',
                        message: 'Product ID not available'
                    })
                    return
                }
                await navigator.clipboard.writeText(productId)
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Product ID copied to clipboard'
                })
            } catch (err) {
                showToast({
                    type: 'error',
                    title: 'Copy Failed',
                    message: 'Failed to copy Product ID'
                })
            }
        }

        /**
         * Copies the tenant domain to the clipboard.
         * @returns {Promise<void>}
         */
        async function copyTenantDomain() {
            try {
                await navigator.clipboard.writeText(tenantDomain.value)
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'X-Tenant-Domain copied to clipboard'
                })
            } catch (err) {
                showToast({
                    type: 'error',
                    title: 'Copy Failed',
                    message: 'Failed to copy X-Tenant-Domain'
                })
            }
        }

        /**
         * Allows only numeric input for an event.
         * @param {Event} event - The input event.
         */
        function onlyNumbers(event) {
            let charCode = (event.which) ? event.which : event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            }
        }

        onMounted(async () => {
            try {
                // Fetch booking engine host from API (sama seperti AuthenticationUrl.vue)
                const apiData = await store.dispatch('Publics/get_booking_engine_api')
                bookingEngineApi.value = apiData
            } catch (error) {
                console.error('Error fetching booking engine API:', error)
                showToast({
                    type: 'error',
                    title: 'Configuration Error',
                    message: 'Failed to load booking engine configuration'
                })
            }

            loadConnection()
        })

        return {
            connection,
            isLoading,
            loadError,
            isSaving,
            isRegenerating,
            isDeleting,
            editData,
            showRegenerateModal,
            showDeleteModal,
            showNewTokenModal,
            showTokenDetailModal,
            newTokenName,
            newToken,
            branchName,
            branchCode,
            merchantName,
            productID,
            tenantDomain,
            bookingEngineApi,
            authUrl,
            statusClass,
            hasChanges,
            canSave,
            loadConnection,
            saveChanges,
            cancelEdit,
            regenerateToken,
            deleteConnection,
            copyAuthUrl,
            copyNewToken,
            copyProductId,
            copyTenantDomain,
            onlyNumbers,
            formatDate,
            goBack,
            isEditingRole,
            selectedRole,
            isUpdatingRole,
            availableRoles,
            isHighPrivilegeRole,
            enableEditRole,
            cancelEditRole,
            updateRole
        }
    }
}
</script>

<style scoped>
/* Container - Gray Background like MarketingDetail */
.connection-detail {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Button Back */
.btn-back {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Loading */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Alert */
.alert {
    padding: 1rem 1.25rem;
    border-radius: 8px;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.alert-success {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
}

/* Detail Content */
.detail-content {
    display: flex;
    flex-direction: column;
    gap: 1rem !important;
}

/* Cards Container - Responsive Grid */
.cards-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

/* Desktop: Side by side with 2/3 and 1/3 ratio */
@media (min-width: 1024px) {
    .cards-container {
        grid-template-columns: 2fr 1fr;
        align-items: start;
    }
}


/* Info Card & Actions Card - White Background */
.info-card,
.actions-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.info-card .card-header,
.actions-card .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem !important;
    margin-bottom: 0 !important;
    background-color: transparent !important;
    border-bottom: none !important;
}

.card-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.card-header-left i {
    color: var(--primary-color, #3b82f6);
    font-size: 1.25rem;
}

.card-header h4 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.status-badge {
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.status-active {
    background: #d1fae5;
    color: #065f46;
}

.status-inactive {
    background: #fee2e2;
    color: #991b1b;
}

.card-body {
    padding: 1.5rem;
}

.info-section {
    margin-bottom: 2rem;
}

.info-section:last-child {
    margin-bottom: 0;
}

.info-section h5 {
    margin-bottom: 1rem;
    color: #374151;
    font-size: 1rem;
    font-weight: 600;
}

/* Card Divider - Gradient fade at edges */
.card-divider {
    height: 1px;
    background: linear-gradient(to right,
            transparent 0%,
            #e5e7eb 5%,
            #e5e7eb 95%,
            transparent 100%);
    margin: 0;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
}

.info-item label {
    display: block;
    margin-bottom: 0.375rem;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
}

.info-item .value {
    font-size: 0.9375rem;
    color: #111827;
    font-weight: 500;
}

.value-with-copy {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.btn-copy-small {
    padding: 0.25rem 0.5rem;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-copy-small:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.form-row .form-group {
    margin-bottom: 0;
}

.form-group {
    margin-bottom: 1.25rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
}

.form-control {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: var(--primary-color, #3b82f6);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
}

/* Input with feedback wrapper */
.input-with-feedback {
    position: relative;
}

.input-with-feedback .form-control {
    padding-right: 2.5rem;
}

.input-feedback-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.input-feedback-icon i {
    font-size: 1.125rem;
}

.text-primary {
    color: var(--primary-color, #3b82f6);
}

.text-success {
    color: #10b981;
}

.text-danger {
    color: #dc2626;
}

/* Validation states */
.form-control.input-checking {
    border-color: var(--primary-color, #3b82f6);
}

.form-control.input-valid {
    border-color: #10b981;
}

.form-control.input-invalid {
    border-color: #dc2626;
}

.error-text {
    display: block;
    color: #dc2626;
    font-size: 0.75rem;
    margin-top: 0.25rem;
}

.auth-url-display {
    display: flex;
    gap: 0.5rem;
}

.btn-copy {
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-copy:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
}

.btn-primary,
.btn-secondary,
.btn-warning,
.btn-danger {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    display: flex;
    align-items: center;
}

.btn-primary {
    background: var(--primary-color, #3b82f6);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: var(--primary-color-dark, #2563eb);
}

.btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}

.btn-secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.btn-secondary:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-warning {
    background: #f59e0b;
    color: white;
}

.btn-warning:hover {
    background: #d97706;
}

.btn-danger {
    background: #dc2626;
    color: white;
}

.btn-danger:hover {
    background: #b91c1c;
}

.action-item {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 1rem;
}

.action-item:last-child {
    margin-bottom: 0;
}

.action-item.danger {
    border-color: #fecaca;
    background: #fef2f2;
}

.action-info h5 {
    margin: 0 0 0.5rem 0;
    color: #111827;
    font-size: 1rem;
}

.action-info p {
    margin: 0;
    color: #6b7280;
    font-size: 0.875rem;
}

.alert {
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    margin-top: 1rem;
}

.alert-success {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-content {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-content.modal-large {
    max-width: 700px;
    width: 90%;
}

.modal-content h4 {
    margin-bottom: 1rem;
    color: #111827;
}

.modal-content p {
    margin-bottom: 1.5rem;
    color: #6b7280;
}

.text-warning {
    color: #92400e;
    background: #fef3c7;
    padding: 0.75rem;
    border-radius: 8px;
}

.token-display {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

/* Link Styling */
.link-primary {
    color: var(--primary-color, #F93E3E) !important;
    text-decoration: none !important;
    font-weight: 700;
    transition: color 0.2s ease;
}

.link-primary:hover {
    color: var(--primary-color, #F93E3E) !important;
    text-decoration: underline !important;
}

/* Token Table Container */
.token-table-container {
    margin: 1rem 0;
}

.token-detail-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    table-layout: fixed;
}

.token-detail-table thead {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
}

.token-detail-table thead th {
    padding: 0.875rem 1rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.token-detail-table tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s ease;
}

.token-detail-table tbody tr:hover {
    background: #f9fafb;
}

.token-detail-table tbody tr:last-child {
    border-bottom: none;
}

.token-detail-table tbody td {
    padding: 1rem;
}

.token-detail-table th:nth-child(1),
.token-detail-table .field-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    width: 30%;
}

.token-detail-table .field-value {
    font-size: 0.875rem;
    color: #111827;
}

.token-detail-table .text-muted {
    color: #9ca3af;
    font-style: italic;
}

/* Token Info Modal (Old Style - keeping for compatibility) */
.token-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1.5rem 0;
}

.token-info .info-row {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.token-info .info-row label {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.token-info .info-row .value {
    font-size: 0.9375rem;
    color: #111827;
    font-weight: 500;
}

.no-token-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    gap: 0.5rem;
}

.no-token-info i {
    font-size: 2rem;
    color: #9ca3af;
}

.no-token-info p {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
}

/* Badges */
.section-divider {
    height: 1px;
    background: linear-gradient(to right,
            transparent 0%,
            #9b9fa6 5%,
            #9b9fa6 95%,
            transparent 100%);
    margin: 2rem 0;
}

.role-badge {
    background: #e5e7eb;
    color: #374151;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.status-badge-small {
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
}

.mt-4 {
    margin-top: 1.5rem;
}

@media (max-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr;
    }

    .form-actions,
    .modal-actions {
        flex-direction: column;
    }
}

/* Role Editing Styles */
.role-display {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-icon-small {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 0.875rem;
    transition: all 0.2s;
    border-radius: 4px;
}

.btn-icon-small:hover {
    color: #3b82f6;
    background-color: #f3f4f6;
}

.role-edit {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
}

.role-select {
    padding: 0.25rem 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.875rem;
    background-color: white;
    min-width: 150px;
}

.role-actions-row {
    display: flex;
    gap: 0.25rem;
}

.btn-xs {
    padding: 0.25rem 0.6rem;
    font-size: 0.75rem;
    border-radius: 4px;
    border: 1px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    font-weight: 500;
}

.role-warning-small {
    font-size: 0.75rem;
    color: #92400e;
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

/* Delete Modal List */
.delete-list {
    list-style: none;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow-y: auto;
    max-height: 280px;
}

.delete-list li {
    padding: 0.625rem 0.875rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.875rem;
    color: #4b5563;
    background-color: #f9fafb;
}

.delete-list li:last-child {
    border-bottom: none;
}

.delete-list li i {
    color: #dc2626;
    width: 20px;
    text-align: center;
    margin-top: 3px;
    flex-shrink: 0;
}

.delete-list li strong {
    color: #1f2937;
    display: block;
    margin-bottom: 0.125rem;
}
</style>
